import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'

/**
 * Transient notifications.
 *
 * Toasts announce through an aria-live region rather than stealing focus — a
 * toast that grabs focus interrupts whatever someone was typing, which on a
 * form is worse than the toast is useful.
 *
 * Errors do not auto-dismiss. A success message can disappear because the
 * result is visible on screen anyway; an error that vanishes before it is read
 * leaves someone with a failed action and no idea why.
 */

const ToastContext = createContext(null)

const TONES = {
  success: { icon: CheckCircle2, color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  error:   { icon: AlertCircle,  color: 'var(--color-danger)',  bg: 'var(--color-danger-bg)' },
  warning: { icon: AlertCircle,  color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  info:    { icon: Info,         color: 'var(--color-info)',    bg: 'var(--color-info-bg)' },
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))

    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const show = useCallback(
    (message, { tone = 'info', title, duration, action } = {}) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

      // Errors persist until dismissed. Everything else clears itself.
      const timeout = duration ?? (tone === 'error' ? 0 : 4500)

      setToasts((current) => {
        // Cap the stack. A failing background poll can otherwise fill the
        // screen with identical messages and hide the page underneath.
        const next = [...current, { id, message, tone, title, action }]
        return next.slice(-4)
      })

      if (timeout > 0) {
        timers.current.set(id, setTimeout(() => dismiss(id), timeout))
      }

      return id
    },
    [dismiss],
  )

  const value = useMemo(
    () => ({
      show,
      dismiss,
      success: (message, options) => show(message, { ...options, tone: 'success' }),
      error:   (message, options) => show(message, { ...options, tone: 'error' }),
      warning: (message, options) => show(message, { ...options, tone: 'warning' }),
      info:    (message, options) => show(message, { ...options, tone: 'info' }),
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}

      {createPortal(
        <div
          // Polite, not assertive: assertive interrupts a screen reader
          // mid-sentence, which is right for an alarm and wrong for "Saved".
          aria-live="polite"
          aria-atomic="false"
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:top-0 sm:bottom-auto sm:items-end"
        >
          {toasts.map((toast) => {
            const tone = TONES[toast.tone] ?? TONES.info
            const Icon = tone.icon

            return (
              <div
                key={toast.id}
                role={toast.tone === 'error' ? 'alert' : 'status'}
                className={clsx(
                  'pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-lg border p-3 shadow-lg',
                  'bg-[var(--color-surface)]',
                )}
                style={{ borderColor: tone.color, borderLeftWidth: '3px' }}
              >
                <span
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: tone.bg }}
                >
                  <Icon size={13} style={{ color: tone.color }} aria-hidden="true" />
                </span>

                <div className="min-w-0 flex-1">
                  {toast.title && (
                    <p className="text-sm font-semibold text-[var(--color-ink-900)]">{toast.title}</p>
                  )}
                  <p className="text-sm leading-snug text-[var(--color-ink-700)]">{toast.message}</p>

                  {toast.action && (
                    <button
                      type="button"
                      onClick={() => {
                        toast.action.onClick()
                        dismiss(toast.id)
                      }}
                      className="mt-1.5 cursor-pointer text-sm font-medium text-[var(--color-accent)] hover:underline"
                    >
                      {toast.action.label}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss notification"
                  className="-mr-1 -mt-0.5 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded text-[var(--color-ink-400)] hover:text-[var(--color-ink-900)]"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)

  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider.')
  }

  return context
}
